import { useState, useMemo } from 'react'
import { Search, ChevronDown, X, Check } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import { cn } from '@/lib/utils/cn'

export interface IconPickerProps {
  /** Selected icon key (kebab-case lucide name). Empty string / null = unselected. */
  value: string | null | undefined
  /** Called with the new icon key (empty string when cleared). */
  onChange: (key: string) => void
  /** Catalogue of icons to choose from. */
  icons: string[]
  /** Optional accent color used to tint the selected icon. */
  color?: string
  /** Allow clearing the selection. Defaults to true. */
  clearable?: boolean
  /** Placeholder shown when no icon picked. */
  placeholder?: string
  /** Disabled state. */
  disabled?: boolean
  /** Trigger size. */
  size?: 'sm' | 'md'
  /** Optional className on the trigger. */
  className?: string
  /** How many columns in the grid. Default 6. */
  columns?: number
  /** Override popover content width class. */
  contentClassName?: string
}

/**
 * Shared icon picker. Trigger looks like a form input. Click opens a popover
 * with searchable grid of icons. Use anywhere in the app that needs an icon
 * selection (projects, form groups, forms, etc.).
 */
export function IconPicker({
  value,
  onChange,
  icons,
  color,
  clearable = true,
  placeholder = 'انتخاب آیکون',
  disabled,
  size = 'md',
  className,
  columns = 6,
  contentClassName,
}: IconPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return icons
    return icons.filter((k) => k.toLowerCase().includes(q))
  }, [icons, search])

  const triggerHeight = size === 'sm' ? 'h-9' : 'h-10'

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors',
            'hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            triggerHeight,
            className,
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {value ? (
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-white"
                style={{ backgroundColor: color ?? 'hsl(var(--primary))' }}
              >
                <DynamicIcon name={value} className="h-3.5 w-3.5" />
              </span>
            ) : (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dashed border-border text-muted-foreground">
                <Search className="h-3 w-3" />
              </span>
            )}
            <span className={cn('truncate', value ? 'font-mono text-xs' : 'text-muted-foreground')}>
              {value || placeholder}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {clearable && value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange('')
                }}
                className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="پاک کردن"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={cn('w-[320px] p-3', contentClassName)}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجو..."
              className="h-9 pr-9"
            />
          </div>

          <div
            className="grid max-h-64 gap-1 overflow-y-auto rounded-md bg-card/40 p-1"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {filtered.length === 0 ? (
              <p className="col-span-full p-4 text-center text-xs text-muted-foreground">
                آیکونی یافت نشد
              </p>
            ) : (
              filtered.map((key) => {
                const selected = value === key
                return (
                  <button
                    key={key}
                    type="button"
                    title={key}
                    onClick={() => {
                      onChange(key)
                      setOpen(false)
                      setSearch('')
                    }}
                    className={cn(
                      'group relative flex aspect-square items-center justify-center rounded-md border transition-colors',
                      selected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-transparent text-foreground hover:border-border hover:bg-muted',
                    )}
                    style={selected && color ? { color, borderColor: color } : undefined}
                  >
                    <DynamicIcon name={key} className="h-4 w-4" />
                    {selected && (
                      <Check
                        className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-primary p-0.5 text-primary-foreground"
                        style={color ? { backgroundColor: color, color: '#fff' } : undefined}
                      />
                    )}
                  </button>
                )
              })
            )}
          </div>

          <div className="flex items-center justify-between border-t border-border pt-2 text-xs text-muted-foreground">
            <span>{filtered.length} مورد</span>
            {clearable && value && (
              <button
                type="button"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                حذف انتخاب
              </button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
